import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { MISSION_WORKFLOW } from "../lib/helpers";
import * as Icons from "lucide-react";

export const PhaseSelector = ({ phases, setPhases, planningMode, setPlanningMode, showPlanning = true, showTemplates = true, showDescription = false, showToValidate = false }) => {
  const [templates, setTemplates] = useState([]);
  const [customLabel, setCustomLabel] = useState("");
  const [dragI, setDragI] = useState(null);

  useEffect(() => {
    if (showTemplates) api.get("/admin/workflow-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [showTemplates]);

  const togglePredef = (w) => {
    setPhases(phases.some((p) => p.id === w.key) ? phases.filter((p) => p.id !== w.key) : [...phases, { id: w.key, label: w.label, custom: false, description: "" }]);
  };
  const addCustom = () => {
    if (!customLabel.trim()) return;
    setPhases([...phases, { id: `custom_${Date.now()}${Math.floor(Math.random() * 1000)}`, label: customLabel.trim(), custom: true, description: "" }]);
    setCustomLabel("");
  };
  const removePhase = (id) => setPhases(phases.filter((p) => p.id !== id));
  const setDesc = (id, v) => setPhases(phases.map((p) => (p.id === id ? { ...p, description: v } : p)));
  const toggleToValidate = (id) => setPhases(phases.map((p) => (p.id === id ? { ...p, to_validate: !p.to_validate } : p)));
  const onDrop = (i) => {
    if (dragI === null || dragI === i) return;
    const a = [...phases]; const [it] = a.splice(dragI, 1); a.splice(i, 0, it); setPhases(a); setDragI(null);
  };
  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (t) setPhases(t.phases.map((p) => ({ ...p })));
  };

  return (
    <div>
      {showTemplates && templates.length > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-bordeaux mb-1">Appliquer un modèle</div>
          <select onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); e.target.value = ""; }} data-testid="apply-template-select" className="w-full text-sm border border-marine/20 rounded-sm p-2">
            <option value="">— Choisir un modèle de workflow —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.phases.length} phases)</option>)}
          </select>
        </div>
      )}

      {showPlanning && (
        <label className="flex items-center gap-2 text-sm text-marine mb-4 cursor-pointer" data-testid="planning-mode-toggle">
          <input type="checkbox" checked={planningMode === "month"} onChange={(e) => setPlanningMode(e.target.checked ? "month" : "date")} />
          Planification au mois uniquement (sinon à la date)
        </label>
      )}

      <div className="text-xs uppercase tracking-wider text-bordeaux mb-2">Phases prédéfinies</div>
      <div className="grid grid-cols-2 gap-1.5 mb-4">
        {MISSION_WORKFLOW.map((w) => (
          <label key={w.key} className="flex items-center gap-2 text-sm text-marine cursor-pointer">
            <input type="checkbox" checked={phases.some((p) => p.id === w.key)} onChange={() => togglePredef(w)} data-testid={`predef-${w.key}`} /> {w.label}
          </label>
        ))}
      </div>

      <div className="text-xs uppercase tracking-wider text-bordeaux mb-2">Phase personnalisée</div>
      <div className="flex gap-2 mb-4">
        <input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} data-testid="custom-phase-input" placeholder="Ex. Tournage extérieur" className="flex-1 border border-marine/20 rounded-sm p-2 text-sm" />
        <button type="button" onClick={addCustom} data-testid="custom-phase-add" className="bg-bordeaux text-white rounded-sm px-3 text-sm">Ajouter</button>
      </div>

      <div className="text-xs uppercase tracking-wider text-bordeaux mb-2">Ordre (glisser-déposer)</div>
      <div className="space-y-1.5" data-testid="phase-order-list">
        {phases.map((p, i) => (
          <div key={p.id} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
            data-testid={`order-item-${p.id}`} className="border border-marine/15 rounded-sm p-2 bg-creme">
            <div draggable onDragStart={() => setDragI(i)} className="flex items-center gap-2 cursor-grab active:cursor-grabbing">
              <Icons.GripVertical size={16} className="text-muet shrink-0" />
              <span className="text-sm text-marine flex-1">{i + 1}. {p.label}</span>
              {p.custom && <span className="text-[9px] uppercase text-bordeaux bg-bordeaux-50 border border-bordeaux/30 px-1.5 py-0.5 rounded-sm">perso</span>}
              <button type="button" onClick={() => removePhase(p.id)} className="text-rouge"><Icons.Trash2 size={16} /></button>
            </div>
            {showDescription && (
              <textarea value={p.description || ""} onChange={(e) => setDesc(p.id, e.target.value)} data-testid={`phase-desc-${p.id}`} rows={2}
                placeholder="Description de la phase (visible sur le portail)…"
                className="w-full mt-2 border border-marine/15 rounded-sm p-2 text-xs text-marine outline-none focus:ring-1 focus:ring-marine" />
            )}
            {showToValidate && (
              <label className="flex items-center gap-1.5 text-[11px] text-marine/80 mt-2 cursor-pointer" title="Le collaborateur pourra valider lui-même cette phase">
                <input type="checkbox" checked={!!p.to_validate} onChange={() => toggleToValidate(p.id)} data-testid={`phase-tovalidate-${p.id}`} /> À valider par le collaborateur
              </label>
            )}
          </div>
        ))}
        {phases.length === 0 && <p className="text-sm text-marine/80">Aucune phase sélectionnée.</p>}
      </div>
    </div>
  );
};
